// @flow

import React, { type Element, useState } from 'react'
import {
  Button,
  makeStyles,
  Collapse,
  IconButton,
  Typography,
  CircularProgress,
  Backdrop,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import { Alert } from '@material-ui/lab'
import { MuiThemeProvider } from '@material-ui/core/styles'
import theme from '../../../theme'
import { validateFields, healthNumber } from './validate'
import { request } from '../../Common/Request'
import { useValidationResult } from '../../../lib/CoreValidations'
import { core } from '../../../lib/WasmCache'

type Props = {|
  callBackPath: string,
  patientId: string,
  userProvince: string,
|}

type Payload = {
  insurance_policy_number: string,
  date_of_birth?: string,
  insurance_version_code?: string,
  id?: string,
  ...
}

const useStyles = makeStyles({
  button: {
    marginRight: '5px',
    textTransform: 'none',
  },
  alert: {
    marginTop: '20px',
  },
  backDrop: {
    zIndex: '3',
  },
})

function FindPatient({
  callBackPath,
  patientId,
  userProvince,
}: Props): Element<typeof MuiThemeProvider> {
  const [, onChange] = useValidationResult(core.validateBcPhn)
  const [isAlert, setIsAlert] = useState(false)
  const [alertType, setAlertType] = useState('error')
  const [message, setMessage] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isMergeButton, setIsMergeButton] = useState(false)
  const formId: string = patientId ? `edit_patient_${patientId}` : 'new_patient'
  const classes = useStyles()

  const patientDetails: HTMLElement | null = document.querySelector('.patient_details')

  function provinceData(): Payload {
    let data = {}
    if (healthNumber) {
      data = Object.assign({}, data, { insurance_policy_number: healthNumber?.value })
    }
    if (patientId) {
      data = Object.assign({}, data, { id: patientId })
    }
    if (userProvince === 'bc') {
      const dateOfBirth: HTMLInputElement = (document.querySelector('#patient_date_of_birth'): any)
      return Object.assign({}, data, { date_of_birth: dateOfBirth?.value })
    }
    if (userProvince === 'on') {
      const versionCode: HTMLInputElement = (document.querySelector('#insurance_version_code'): any)
      const user_id: HTMLInputElement = (document.querySelector(
        '.js-patient_referrals-form .js-referral-user'
      ): any)

      return Object.assign({}, data, {
        insurance_version_code: versionCode?.value,
        patient_referral_user_id: user_id?.value,
      })
    }
    return data
  }

  // patient details form
  function showForm(): void {
    setIsAlert(true)
    setIsLoading(false)
    if (patientDetails) patientDetails.style.display = 'block'
  }

  function setGender(active, disable): void {
    if (active) {
      active.checked = true
      active.closest('.btn')?.classList.add('active')
    }
    if (disable) {
      disable.checked = false
      disable.closest('.btn')?.classList.remove('active')
    }
  }

  function handleError(errors: { [string]: Array<string> }): void {
    setMessage(errors.non_field_errors)
    setAlertType('error')

    showForm()
  }

  function handleSuccess(data): void {
    const fName: HTMLInputElement = (document.querySelector('#patient_first_name'): any)
    const lName: HTMLInputElement = (document.querySelector('#patient_last_name'): any)
    const dateOfBirth: HTMLInputElement = (document.querySelector('#patient_date_of_birth'): any)
    const versionCode: HTMLInputElement = (document.querySelector('#insurance_version_code'): any)
    const male: HTMLInputElement = (document.querySelector('#patient_gender_M'): any)
    const female: HTMLInputElement = (document.querySelector('#patient_gender_F'): any)
    const mergeIdField: HTMLInputElement = (document.querySelector('#merge_id'): any)

    if (fName) fName.value = data.first_name
    if (lName) lName.value = data.last_name
    if (data.date_of_birth && dateOfBirth) dateOfBirth.value = data.date_of_birth
    if (data.version_code && versionCode) versionCode.value = data.version_code
    if (data.existing_patient_id && patientId) {
      if (mergeIdField) mergeIdField.value = data.existing_patient_id
      setIsMergeButton(true)
    }

    data.gender === 'M' ? setGender(male, female) : setGender(female, male)
    data.eligible ? setAlertType('success') : setAlertType(data.first_name ? 'warning' : 'error')
    setMessage(data.message)

    showForm()
  }

  function searchPatient(): Promise<boolean> {
    const payload = provinceData()

    return request(callBackPath, 'POST', {}, JSON.stringify(payload), handleError, handleSuccess)
  }

  function handleClick(): void {
    setIsAlert(false)
    setIsMergeButton(false)

    if (validateFields(userProvince, onChange)) {
      if (patientDetails) patientDetails.style.display = 'none'
      setIsLoading(true)

      searchPatient()
    }
  }

  return (
    <MuiThemeProvider theme={theme}>
      <Button
        classes={{ root: classes.button }}
        variant="outlined"
        color="secondary"
        onClick={handleClick}
      >
        {isLoading ? 'Loading..' : 'Find Patient'}
      </Button>
      {isMergeButton && patientId && (
        <Button
          classes={{ root: classes.button }}
          variant="outlined"
          color="primary"
          type="submit"
          form={formId}
        >
          Merge
        </Button>
      )}
      <Collapse in={isAlert}>
        <Alert
          classes={{ root: classes.alert }}
          icon={false}
          severity={alertType}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                setIsAlert(false)
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          <Typography color="inherit"> {message} </Typography>
        </Alert>
      </Collapse>
      <Backdrop classes={{ root: classes.backDrop }} open={isLoading}>
        <CircularProgress color="primary" />
      </Backdrop>
    </MuiThemeProvider>
  )
}

export default FindPatient
